import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Users,
    FolderKanban,
    FileText,
    Settings,
    Bell,
    Search,
    TrendingUp,
    TrendingDown,
    Calendar,
    Target,
    CheckCircle,
    Clock,
    AlertCircle,
    BarChart3,
    ArrowUpRight,
    Menu,
    X,
    LogOut,
    MessageSquare,
    Shield,
    Plus
} from "lucide-react";
import { getCurrentUser, logoutUser } from "../../utils/auth";
import dashboardImg from "../../imgs/dashboard.svg";

const ProjectLeaderDashboard = () => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const user = getCurrentUser();
    const navigate = useNavigate();

    const handleLogout = () => {
        logoutUser();
        navigate("/login");
    };

    const menuItems = [
        { path: "/project-leader/manage-projects", label: "Manage Projects", icon: <FolderKanban className="w-5 h-5" /> },
        { path: "/project-leader/audit-logs", label: "Audit Logs", icon: <FileText className="w-5 h-5" /> },
        { path: "/project-leader/team-management", label: "Team Management", icon: <Users className="w-5 h-5" /> },
        { path: "/project-leader/settings", label: "Settings", icon: <Settings className="w-5 h-5" /> },
    ];

    const stats = [
        {
            label: "Active Projects",
            value: "12",
            change: "+3.2%",
            trend: "up",
            icon: <FolderKanban className="w-6 h-6 text-blue-600" />,
            color: "from-blue-500 to-blue-600"
        },
        {
            label: "Team Members",
            value: "28",
            change: "+5.1%",
            trend: "up",
            icon: <Users className="w-6 h-6 text-green-600" />,
            color: "from-green-500 to-green-600"
        },
        {
            label: "Completed Tasks",
            value: "156",
            change: "+12.5%",
            trend: "up",
            icon: <CheckCircle className="w-6 h-6 text-purple-600" />,
            color: "from-purple-500 to-purple-600"
        },
        {
            label: "Pending Reviews",
            value: "8",
            change: "-2.3%",
            trend: "down",
            icon: <Clock className="w-6 h-6 text-yellow-600" />,
            color: "from-yellow-500 to-yellow-600"
        },
    ];

    const recentProjects = [
        { name: "Website Redesign", progress: 85, status: "on-track", deadline: "Dec 15, 2024", team: "Design Team" },
        { name: "Mobile App V2", progress: 60, status: "at-risk", deadline: "Jan 10, 2025", team: "Dev Team" },
        { name: "API Integration", progress: 95, status: "on-track", deadline: "Nov 30, 2024", team: "Backend Team" },
        { name: "Analytics Dashboard", progress: 45, status: "delayed", deadline: "Dec 20, 2024", team: "Data Team" },
    ];

    const teamActivities = [
        { member: "Alex Chen", action: "Completed homepage design", time: "2 hours ago", avatar: "AC" },
        { member: "Maria Rodriguez", action: "Submitted code review", time: "3 hours ago", avatar: "MR" },
        { member: "David Kim", action: "Updated project documentation", time: "5 hours ago", avatar: "DK" },
        { member: "Sarah Wilson", action: "Resolved API bug", time: "1 day ago", avatar: "SW" },
    ];

    const upcomingDeadlines = [
        { task: "Design Review", project: "Website Redesign", due: "Tomorrow", priority: "high" },
        { task: "API Documentation", project: "Mobile App V2", due: "In 2 days", priority: "medium" },
        { task: "Team Meeting", project: "All Projects", due: "Friday", priority: "low" },
    ];

    const getStatusColor = (status) => {
        switch (status) {
            case "on-track": return "bg-green-100 text-green-800";
            case "at-risk": return "bg-yellow-100 text-yellow-800";
            case "delayed": return "bg-red-100 text-red-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case "high": return "bg-red-100 text-red-800";
            case "medium": return "bg-yellow-100 text-yellow-800";
            case "low": return "bg-green-100 text-green-800";
            default: return "bg-gray-100 text-gray-800";
        }
    };

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar */}
            <motion.aside
                initial={{ x: sidebarOpen ? 0 : -256 }}
                animate={{ x: sidebarOpen ? 0 : -256 }}
                className="fixed lg:relative h-full bg-white border-r border-gray-200 z-40 transition-all duration-300"
            >
                <div className="h-full flex flex-col w-64">
                    {/* Logo */}
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                                    <Target className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold text-gray-900">TeamHub</h1>
                                    <p className="text-xs text-gray-500">Project Leader</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setSidebarOpen(false)}
                                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4">
                        <div className="space-y-1">
                            {menuItems.map((item, index) => (
                                <motion.div
                                    key={index}
                                    whileHover={{ x: 4 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                                >
                                    <Link
                                        to={item.path}
                                        className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors group"
                                    >
                                        <div className="text-gray-400 group-hover:text-blue-600">
                                            {item.icon}
                                        </div>
                                        <span className="font-medium">{item.label}</span>
                                    </Link>
                                </motion.div>
                            ))}
                        </div>

                        {/* Team Quick Access */}
                        <div className="mt-8">
                            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 mb-3">
                                Quick Access
                            </h3>
                            <div className="space-y-1">
                                <button className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors w-full text-left">
                                    <MessageSquare className="w-5 h-5" />
                                    <span className="font-medium">Team Messages</span>
                                </button>
                                <button className="flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-700 hover:text-blue-600 hover:bg-blue-50 transition-colors w-full text-left">
                                    <BarChart3 className="w-5 h-5" />
                                    <span className="font-medium">Reports</span>
                                </button>
                            </div>
                        </div>
                    </nav>

                    {/* User Profile */}
                    <div className="p-4 border-t border-gray-200">
                        <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="relative">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {user?.email?.charAt(0).toUpperCase() || 'P'}
                  </span>
                                </div>
                                <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {user?.email || 'Project Leader'}
                                </p>
                                <p className="text-xs text-gray-500">Team Leader</p>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Logout"
                            >
                                <LogOut className="w-4 h-4 text-gray-400 hover:text-red-500" />
                            </button>
                        </div>
                    </div>
                </div>
            </motion.aside>

            {/* Overlay for mobile */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 lg:hidden z-30"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Main Content */}
            <main className="flex-1 overflow-auto">
                {/* Header */}
                <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
                    <div className="px-6 py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={() => setSidebarOpen(true)}
                                    className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
                                >
                                    <Menu className="w-5 h-5" />
                                </button>
                                <div className="relative flex-1 max-w-md">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search projects, tasks, or team members..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center space-x-4">
                                <button className="relative p-2 hover:bg-gray-100 rounded-lg">
                                    <Bell className="w-5 h-5 text-gray-600" />
                                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                                </button>
                                <button className="p-2 hover:bg-gray-100 rounded-lg">
                                    <Calendar className="w-5 h-5 text-gray-600" />
                                </button>
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg" />
                            </div>
                        </div>
                    </div>
                </header>

                {/* Dashboard Content */}
                <div className="p-6">
                    {/* Welcome Banner */}
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                                    Project Dashboard
                                </h1>
                                <p className="text-gray-600">
                                    Welcome back, {user?.email?.split('@')[0] || 'Leader'}. Here's your project overview.
                                </p>
                            </div>
                            <button className="btn-primary">
                                <Plus className="w-5 h-5 mr-2" />
                                New Project
                            </button>
                        </div>
                    </motion.div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="card hover:shadow-medium"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`p-3 rounded-xl bg-gradient-to-br ${stat.color} bg-opacity-10`}>
                                        {stat.icon}
                                    </div>
                                    <div className={`flex items-center text-sm font-medium ${
                                        stat.trend === "up" ? "text-green-600" : "text-red-600"
                                    }`}>
                                        {stat.trend === "up" ? (
                                            <TrendingUp className="w-4 h-4 mr-1" />
                                        ) : (
                                            <TrendingDown className="w-4 h-4 mr-1" />
                                        )}
                                        {stat.change}
                                    </div>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                                <p className="text-sm text-gray-600">{stat.label}</p>
                            </motion.div>
                        ))}
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6 mb-8">
                        {/* Recent Projects */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="lg:col-span-2"
                        >
                            <div className="card">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl font-bold text-gray-900">Recent Projects</h2>
                                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                                        View all projects
                                    </button>
                                </div>
                                <div className="space-y-4">
                                    {recentProjects.map((project, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="p-4 border border-gray-200 rounded-xl hover:border-blue-300 transition-colors"
                                        >
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <FolderKanban className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <h3 className="font-semibold text-gray-900">{project.name}</h3>
                                                        <p className="text-sm text-gray-500">{project.team}</p>
                                                    </div>
                                                </div>
                                                <span className={`badge ${getStatusColor(project.status)}`}>
                          {project.status.replace('-', ' ')}
                        </span>
                                            </div>
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-600">Progress</span>
                                                    <span className="font-medium text-gray-900">{project.progress}%</span>
                                                </div>
                                                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                                                        style={{ width: `${project.progress}%` }}
                                                    />
                                                </div>
                                                <div className="flex justify-between text-sm text-gray-500">
                                                    <span>Deadline: {project.deadline}</span>
                                                    <button className="text-blue-600 hover:text-blue-700 font-medium">
                                                        Details
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* Team Activities */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                        >
                            <div className="card h-full">
                                <h2 className="text-xl font-bold text-gray-900 mb-6">Team Activities</h2>
                                <div className="space-y-4">
                                    {teamActivities.map((activity, index) => (
                                        <motion.div
                                            key={index}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                <span className="text-blue-700 font-semibold">{activity.avatar}</span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900">{activity.member}</p>
                                                <p className="text-sm text-gray-600 truncate">{activity.action}</p>
                                            </div>
                                            <span className="text-xs text-gray-500 whitespace-nowrap">{activity.time}</span>
                                        </motion.div>
                                    ))}
                                </div>

                                <div className="mt-6 pt-6 border-t border-gray-200">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
                                    <div className="space-y-3">
                                        {upcomingDeadlines.map((deadline, index) => (
                                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                                <div>
                                                    <p className="font-medium text-gray-900">{deadline.task}</p>
                                                    <p className="text-sm text-gray-600">{deadline.project}</p>
                                                </div>
                                                <div className="flex items-center space-x-2">
                          <span className={`badge ${getPriorityColor(deadline.priority)}`}>
                            {deadline.priority}
                          </span>
                                                    <span className="text-sm text-gray-600">{deadline.due}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Quick Actions & Dashboard Image */}
                    <div className="grid lg:grid-cols-2 gap-6">
                        {/* Quick Actions */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <div className="card h-full">
                                <h2 className="text-xl font-bold text-gray-900 mb-6">Quick Actions</h2>
                                <div className="grid grid-cols-2 gap-4">
                                    {menuItems.map((item, index) => (
                                        <motion.div
                                            key={index}
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                        >
                                            <Link
                                                to={item.path}
                                                className="card group hover:border-blue-300 text-center p-4"
                                            >
                                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                                                    <div className="text-blue-600">
                                                        {item.icon}
                                                    </div>
                                                </div>
                                                <h3 className="font-semibold text-gray-900">{item.label}</h3>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        {/* Dashboard Image & Status */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3 }}
                        >
                            <div className="card h-full">
                                <div className="text-center">
                                    <img
                                        src={dashboardImg}
                                        alt="Project Dashboard"
                                        className="w-full max-w-xs mx-auto mb-4 rounded-lg"
                                    />
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Project Command Center
                                    </h3>
                                    <p className="text-gray-600 text-sm mb-6">
                                        Oversee projects, manage teams, and drive successful outcomes with real-time insights.
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <AlertCircle className="w-5 h-5 text-yellow-600" />
                                            <span className="font-medium text-gray-700">System Status</span>
                                        </div>
                                        <span className="badge-success">Operational</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <Shield className="w-5 h-5 text-green-600" />
                                            <span className="font-medium text-gray-700">Security</span>
                                        </div>
                                        <span className="badge-success">Protected</span>
                                    </div>
                                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div className="flex items-center space-x-3">
                                            <BarChart3 className="w-5 h-5 text-blue-600" />
                                            <span className="font-medium text-gray-700">Performance</span>
                                        </div>
                                        <span className="badge-success">Optimal</span>
                                    </div>
                                </div>

                                <button className="w-full mt-6 btn-primary">
                                    <ArrowUpRight className="w-5 h-5 mr-2" />
                                    Generate Performance Report
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default ProjectLeaderDashboard;